"use client"

import * as React from "react"
import { Clock } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Button } from "./button"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select"

interface TimePickerProps {
  value?: string // "HH:MM" format
  onChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  presets?: string[] // Array of preset times like ["09:00", "12:00", "17:00"]
}

// Default presets for common business hours
const DEFAULT_PRESETS = ["09:00", "10:00", "12:00", "13:00", "14:00", "17:00", "18:00", "20:00"]

// Generate hour options (0-23)
const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))

// Generate minute options (0, 5, 10, ... 55)
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'))

export function TimePicker({
  value = "",
  onChange,
  placeholder = "Select time",
  disabled = false,
  className,
  presets = DEFAULT_PRESETS,
}: TimePickerProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = React.useState(false)

  // Parse current value
  const [hours, minutes] = React.useMemo(() => {
    if (!value) return ["09", "00"]
    const parts = value.split(":")
    return [parts[0] || "09", parts[1] || "00"]
  }, [value])

  // Format time for display (24h)
  const displayValue = React.useMemo(() => {
    if (!value) return ""
    const [h, m] = value.split(":")
    return `${h.padStart(2, '0')}:${m}`
  }, [value])

  const handleHourChange = (newHour: string) => {
    const newValue = `${newHour}:${minutes}`
    onChange?.(newValue)
  }

  const handleMinuteChange = (newMinute: string) => {
    const newValue = `${hours}:${newMinute}`
    onChange?.(newValue)
  }

  const handlePresetClick = (preset: string) => {
    onChange?.(preset)
    setOpen(false)
  }

  // Format preset for display (24h)
  const formatPreset = (preset: string) => preset

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal min-h-[44px]",
            !value && "text-muted-foreground",
            className
          )}
        >
          <Clock className="mr-2 h-4 w-4" />
          {displayValue || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-4">
          {/* Header */}
          <div className="text-sm font-medium text-center">{t('timePicker.selectTime')}</div>

          {/* Hour and Minute selectors */}
          <div className="flex items-center justify-center gap-2">
            <Select value={hours} onValueChange={handleHourChange}>
              <SelectTrigger className="w-20">
                <SelectValue placeholder="HH" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {HOURS.map((hour) => (
                  <SelectItem key={hour} value={hour}>
                    {hour}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <span className="text-2xl font-bold text-gray-400">:</span>

            <Select value={minutes} onValueChange={handleMinuteChange}>
              <SelectTrigger className="w-20">
                <SelectValue placeholder="MM" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {MINUTES.map((minute) => (
                  <SelectItem key={minute} value={minute}>
                    {minute}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Current selection display */}
          {value && (
            <div className="text-center text-lg font-semibold text-blue-600">
              {displayValue}
            </div>
          )}

          {/* Preset buttons */}
          <div className="space-y-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t('timePicker.quickSelect')}
            </div>
            <div className="grid grid-cols-4 gap-2">
              {presets.map((preset) => (
                <Button
                  key={preset}
                  variant={value === preset ? "primary" : "outline"}
                  size="sm"
                  onClick={() => handlePresetClick(preset)}
                  className="text-xs"
                >
                  {formatPreset(preset)}
                </Button>
              ))}
            </div>
          </div>

          {/* Done button */}
          <Button
            className="w-full"
            onClick={() => setOpen(false)}
          >
            {t('timePicker.done')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { DEFAULT_PRESETS }
